import React, { ComponentProps, PropsWithChildren } from 'react';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import BaseDialog from '@mui/material/Dialog';

import { expandedPlaylistHeight, jwPlayerModalZIndex, playlistHeight } from '~/styles';

import { usePlaylist } from '../PlayListPlayer';

type Props = {
  open: boolean;
  onClose(): void;
};

type StyledDialogStyles = {
  height: string | null;
};

type StyledDialog = (
  props: ComponentProps<typeof BaseDialog> & StyledDialogStyles,
) => ReturnType<typeof BaseDialog>;

const FULL_SCREEN_HEIGHT = '100%';

const Dialog = styled<StyledDialog>(BaseDialog)`
  z-index: ${jwPlayerModalZIndex};
  .MuiDialog-container > .MuiPaper-root {
    background: black;
    width: 100%;
    height: ${({ height }) => height ?? FULL_SCREEN_HEIGHT};
    transition: ${({ height }) => (!height ? 'all 0.3s ease' : 'all 0.7s ease')};
    max-height: unset;
    max-width: unset;
    border-radius: unset;
    margin: 0;
  }
  .MuiDialog-container {
    align-items: start;
  }
` as StyledDialog;

const CloseButton = styled.button`
  position: absolute;
  z-index: 1;
  cursor: pointer;
  padding: 0;
  background: none;
  border: none;
  font-size: 28px;
  line-height: 18px;
  padding: 4px;
  top: 12px;
  right: 12px;
  color: white;
  svg {
    font-size: 1em;
  }
`;

function useHeight() {
  const { playlistEnabled, showDetailView: isExpanded } = usePlaylist();

  const height = playlistEnabled
    ? isExpanded
      ? `calc(${FULL_SCREEN_HEIGHT} - ${expandedPlaylistHeight})`
      : `calc(${FULL_SCREEN_HEIGHT} - ${playlistHeight})`
    : null;
  return height;
}

export const MediaPlayerModal: React.FC<PropsWithChildren<Props>> = ({
  onClose,
  open,
  children,
}) => {
  const height = useHeight();

  return (
    <Dialog open={open} onClose={onClose} height={height}>
      <>
        <CloseButton aria-label="close" data-testid="class-modal-close" onClick={onClose}>
          <CloseIcon viewBox="5 5 14 14" />
        </CloseButton>
        {children}
      </>
    </Dialog>
  );
};
